const SKIP = "textarea,script,style,code,pre,[contenteditable='true'],[data-user-content],[data-no-terms]";
const ATTRS = ["placeholder", "title", "aria-label"];
const SINGULAR_CTX = /(nuov[oa]|nessun[a]?|tu[oa]|su[oa]|un[a']?|l'|la|il|lo|quest[oa]|dell[a']|del|ogni|singol[oa])\s*$/i;

const matchCase = (src, out) => {
  if (src.length > 1 && src === src.toUpperCase()) return out.toUpperCase();
  if (src[0] === src[0].toUpperCase()) return out[0].toUpperCase() + out.slice(1);
  return out.length > 1 && out[1] === out[1].toUpperCase() ? out : out[0].toLowerCase() + out.slice(1);
};

const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const PAIRS = [["nuovo", "nuova"], ["nessun", "nessuna"], ["un", "una"], ["il", "la"], ["questo", "questa"], ["del", "della"], ["al", "alla"],
  ["dal", "dalla"], ["nel", "nella"], ["sul", "sulla"], ["nuovi", "nuove"], ["i", "le"], ["dei", "delle"], ["ai", "alle"], ["dai", "dalle"], ["nei", "nelle"],
  ["questi", "queste"], ["selezionato", "selezionata"], ["selezionati", "selezionate"], ["tutti", "tutte"], ["vostro", "vostra"], ["nostro", "nostra"], ["tuo", "tua"], ["suo", "sua"]];
const TO_F = Object.fromEntries(PAIRS);
const TO_M = Object.fromEntries(PAIRS.map(([m, f]) => [f, m]));
const ARTS = Object.keys({ ...TO_F, ...TO_M }).sort((a, b) => b.length - a.length);
const ELIDED = { l: ["il", "la"], dell: ["del", "della"], all: ["al", "alla"], nell: ["nel", "nella"], sull: ["sul", "sulla"], dall: ["dal", "dalla"], un: ["un", "una"] };
const TO_ELIDED = { il: "l", lo: "l", la: "l", del: "dell", della: "dell", al: "all", alla: "all", nel: "nell", nella: "nell", sul: "sull", sulla: "sull", dal: "dall", dalla: "dall", una: "un" };
const VOWEL = /^[aeiouhàèéìòù]/i;

const articleFor = (elid, apos, g, word) => {
  const low = elid.toLowerCase();
  if (VOWEL.test(word)) return low === "un" && g === "m" ? matchCase(elid, "un") + " " : elid + apos;
  return matchCase(elid, ELIDED[low][g === "m" ? 0 : 1]) + " ";
};

const elideLast = (pre, word) => {
  if (!VOWEL.test(word)) return pre;
  return pre.replace(/(\p{L}+)\s+$/u, (all, w) => (TO_ELIDED[w.toLowerCase()] ? matchCase(w, TO_ELIDED[w.toLowerCase()]) + (w.toLowerCase() === "una" ? "'" : "'") : all));
};

export const genderOf = (word) => (/[aà]$/i.test(word.trim().split(/\s+/)[0]) ? "f" : "m");

// rules: [{ one, many, newOne, newMany }]; many null = parola solo singolare
export const buildConverter = (rules) => {
  const dict = {};
  rules.forEach((r) => {
    if (r.many && r.many === r.one) dict[r.one] = { amb: true, r };
    else {
      dict[r.one] = { plural: false, r };
      if (r.many) dict[r.many] = { plural: true, r };
    }
  });
  const words = Object.keys(dict).sort((a, b) => b.length - a.length);
  if (!words.length) return null;
  const elid = Object.keys(ELIDED).join("|");
  const re = new RegExp(`(?<!\\p{L})((?:(?:${ARTS.join("|")})\\s+){0,2})(?:(${elid})(['’]))?(${words.map(escape).join("|")})(?!\\p{L})`, "giu");
  return (text) => text.replace(re, (all, pre, el, apos, m, off, s) => {
    const { amb, plural, r } = dict[m.toLowerCase()];
    const ctx = pre || s.slice(Math.max(0, off - 14), off);
    const pl = amb ? !SINGULAR_CTX.test(ctx) : plural;
    const word = matchCase(m, pl ? r.newMany : r.newOne);
    const g = genderOf(r.newOne);
    if (el) return pre + (pl ? el + apos : articleFor(el, apos, g, word)) + word;
    const to = r.gender === g ? null : (r.gender === "m" ? TO_F : TO_M);
    const swapped = to ? pre.replace(/\p{L}+/gu, (a) => (to[a.toLowerCase()] ? matchCase(a, to[a.toLowerCase()]) : a)) : pre;
    return (pl ? swapped : elideLast(swapped, word)) + word;
  });
};

export const startTermTranslator = (convert) => {
  if (!convert) return () => {};
  const seenText = new WeakMap();
  const seenAttr = new WeakMap();
  const doText = (n) => {
    if (seenText.get(n) === n.data) return;
    const p = n.parentElement;
    if (p && !p.closest(SKIP)) {
      const out = convert(n.data);
      if (out !== n.data) n.data = out;
    }
    seenText.set(n, n.data);
  };
  const doAttrs = (el) => {
    if (el.closest(SKIP)) return;
    const seen = seenAttr.get(el) || {};
    ATTRS.forEach((a) => {
      const v = el.getAttribute(a);
      if (!v || seen[a] === v) return;
      const out = convert(v);
      if (out !== v) el.setAttribute(a, out);
      seen[a] = out;
    });
    seenAttr.set(el, seen);
  };
  const walk = (root) => {
    if (root.nodeType === 3) { doText(root); return; }
    if (root.nodeType !== 1) return;
    doAttrs(root);
    const w = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
    for (let n = w.nextNode(); n; n = w.nextNode()) {
      if (n.nodeType === 3) doText(n); else doAttrs(n);
    }
  };
  walk(document.body);
  const obs = new MutationObserver((list) => {
    list.forEach((m) => {
      if (m.type === "characterData") doText(m.target);
      else if (m.type === "attributes") doAttrs(m.target);
      else m.addedNodes.forEach(walk);
    });
  });
  obs.observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ATTRS });
  return () => obs.disconnect();
};
